import { MapUtils } from '../../utility/map-utils.js';
import { Language } from '../serializable/language.js';
import { RichDescription } from './rich-description.js';
import { cloneMap, DataModel, DataModelBuilder, isSameData, isSameMap,
  mapToData, SerializationContext, SerializedData,
} from '../dto/dto.js';
import { toEnumFromString, fromEnumAsString } from '../../serialization/enum-utils.js';
import { VersionRange } from '../core/version-range.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../utility/null-safety.js';
import { getAnyFromLocalizationMap } from '../../utility/lang-utils.js';

export class LocalizedRichDescription extends DataModel {
  static readonly TYPE_ID: string = 'd83e3182e6e64bc4a8c4ffd08faa464a';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public localizations: Map<Language, RichDescription>,
  ) {
    super();
  }

  get $typeId(): string {
    return LocalizedRichDescription.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(LocalizedRichDescription.SCHEMA_VERSION);
  }

  isEmpty(): boolean {
    if(this.localizations.size == 0) {
      return true;
    }

    let isEmpty: boolean = true;
    for(const value of this.localizations.values()) {
      if(value.isNotEmpty) {
        isEmpty = false;
        break;
      }
    }

    return isEmpty;
  }

  isNotEmpty(): boolean {
    return !this.isEmpty();
  }

  has(language: Language): boolean {
    return isNotNullOrUndefined(this.localizations.get(language));
  }

  hasAndNotEmpty(language: Language): boolean {
    const value = this.localizations.get(language);
    return isNotNullOrUndefined(value) && value.isNotEmpty;
  }

  set(language: Language, value: RichDescription) {
    this.localizations.set(language, value);
  }

  get(language: Language): RichDescription | null {
    return this.localizations.get(language) ?? null;
  }

  getAny(language: Language | null = null): RichDescription | null {
    return getAnyFromLocalizationMap(this.localizations, language, (v) => v.isNotEmpty);
  }

  getAnyOrEmpty(language: Language | null = null): RichDescription {
    return getAnyFromLocalizationMap(
        this.localizations,
        language,
        (v) => v.isNotEmpty
    ) ?? RichDescription.empty();
  }

  static empty(): LocalizedRichDescription {
    return new LocalizedRichDescription(
        new Map<Language, RichDescription>(),
    );
  }

  static forLanguage(language: Language, description: RichDescription): LocalizedRichDescription {
    return new LocalizedRichDescription(new Map<Language, RichDescription>([[language, description]]));
  }

  static localized(localizations: Map<Language, RichDescription>) {
    return new LocalizedRichDescription(localizations);
  }

  static fromMap(context: SerializationContext, map: SerializedData): LocalizedRichDescription {
    return new LocalizedRichDescription(
        MapUtils.getMap<Language, RichDescription>(map, 'localizations', (k, v) => {
          return { key: toEnumFromString(k, Language), value: context.fromData<RichDescription>(v as SerializedData) };
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'localizations': mapToData(this.localizations, (k, v) => {
        return { key: fromEnumAsString(k), value: context.toData(v) };
      }),
    };
  }

  clone(): this {
    return new LocalizedRichDescription(
        cloneMap(this.localizations, (localizations) => localizations.clone()),
    ) as this;
  }

  isSame(other: LocalizedRichDescription): boolean {
    if(other === this) {
      return true;
    }

    return isSameMap(other.localizations, this.localizations, (left, right) => isSameData(left, right));
  }
}

export class LocalizedRichDescriptionBuilder extends DataModelBuilder<LocalizedRichDescription> {
  build(context: SerializationContext, map: SerializedData): LocalizedRichDescription {
    return LocalizedRichDescription.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(LocalizedRichDescription.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(LocalizedRichDescription.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we removed the [description] field.
    if(version < 2) {
      version = 2;

      const language = context.storage.get<Language>('language', Language.English);
      if(isNotNullOrUndefined(language)) {
        const description = context.fromData<RichDescription>(MapUtils.get<SerializedData>(map, 'description'));
        const localizedDescription = MapUtils.getMap<Language, RichDescription>(map, 'localizedDescription', (k, v) => {
          return { key: toEnumFromString(k, Language), value: context.fromData<RichDescription>(v as SerializedData) };
        });

        if(isNullOrUndefined(localizedDescription.get(language))) {
          localizedDescription.set(language, description);
        }

        map['localizedDescription'] = mapToData(localizedDescription, (k, v) => {
          return { key: fromEnumAsString(k), value: context.toData(v) };
        });
      }
    }

    // In this version we renamed [localizedDescription] to [localizations].
    if(version < 3) {
      version = 3;
      map['localizations'] = map['localizedDescription'];
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
